import angular from 'angular';
import {
    DIGITAL_RULE_EVENTS,
    EVENT_ALBUM_EVENTS,
    EVENT_EVENTS,
    GOOGLE_PHOTOS_EXPORT_EVENTS,
    PHOTO_DOWNLOAD_EVENTS,
    VIDEO_DOWNLOAD_EVENTS,
    ZIP_BUNDLE_EVENTS
} from 'Sp/Angular/Events/Client/Gallery';
import { findBy, pluck } from 'Sp/Array';
import { DOWNLOAD_DESTINATION, DOWNLOAD_TYPE } from 'Sp/Gallery';

/**
 * @ngdoc service
 * @name sp.client.gallery.service:spFreeDigitals
 * @requires $rootScope
 * @requires $routeParams
 * @requires $window
 * @requires SPModal
 * @requires spClientCredentials
 * @requires translateFilter
 *
 * @description
 * Handles permissions around, information about, and ability to download free digitals
 */
export default [
    '$rootScope',
    '$routeParams',
    '$window',
    '$timeout',
    'SPModal',
    'spClientCredentials',
    'spHiddenAlbum',
    'spAppData',
    'spToast',
    'translateFilter',
    'spDevice',
    'spAuthorizationToken',
    function spFreeDigitalsService(
        $rootScope,
        $routeParams,
        $window,
        $timeout,
        SPModal,
        spClientCredentials,
        spHiddenAlbum,
        spAppData,
        spToast,
        translateFilter,
        spDevice,
        spAuthorizationToken
    ) {
        const PIN_LENGTH = {
            MAXIMUM: 8,
            MINIMUM: 4
        };
        const { noop } = angular;
        const state = {
            authorizationToken: null,
            eventAuthorizationToken: null,
            pendingDownload: {},
            credentials: {
                email: null,
                pins: [],
                doesAcceptTerms: false
            },
            event: {
                hasDigitalRules: false,
                hasDigitalRulesForPhotos: false,
                hasDigitalRulesForVideos: false,
                hasDigitalRulesForSlideshows: false,
                hasDigitalRulesForDownloadAll: false,
                downloadAllRuleMediaTypes: [],
                hasPinForDigitalRules: false,
                maximumDownloadAllPhotoCount: null,
                hasAccessToZipBundleDigitalRules: false,
                hasAccessToPhotoDownloadDigitalRules: false
            },
            eventAlbum: {
                hasDigitalRules: false,
                hasDigitalRulesForPhotos: false,
                hasDigitalRulesForVideos: false,
                hasDigitalRulesForSlideshows: false,
                hasDigitalRulesForDownloadAll: false,
                downloadAllRuleMediaTypes: [],
                hasPinForDigitalRules: false,
                maximumDownloadAllPhotoCount: null,
                hasAccessToZipBundleDigitalRules: false,
                hasAccessToPhotoDownloadDigitalRules: false
            },
            digitalRules: null,
            digitalRuleCount: 0,
            selectedDigitalRule: null,
            isEventContact: false,
            downloadForRender: spDevice.deviceIsIOs()
        };

        let afterPinCapturedCallback = noop;
        let isCapturingPin = false;

        function canDownloadPhoto() {
            if (!state.eventAlbum.hasDigitalRulesForPhotos && spHiddenAlbum.isInFlow()) {
                return false;
            }

            return (
                (state.event.hasDigitalRulesForPhotos ||
                    state.eventAlbum.hasDigitalRulesForPhotos) &&
                (state.event.hasAccessToPhotoDownloadDigitalRules ||
                    state.eventAlbum.hasAccessToPhotoDownloadDigitalRules)
            );
        }

        function canDownloadVideo() {
            if (!state.eventAlbum.hasDigitalRulesForVideos && spHiddenAlbum.isInFlow()) {
                return false;
            }

            return (
                (state.event.hasDigitalRulesForVideos ||
                    state.eventAlbum.hasDigitalRulesForVideos) &&
                (state.event.hasAccessToPhotoDownloadDigitalRules ||
                    state.eventAlbum.hasAccessToPhotoDownloadDigitalRules)
            );
        }

        function canDownloadSlideshow() {
            if (!state.eventAlbum.hasDigitalRulesForSlideshows && spHiddenAlbum.isInFlow()) {
                return false;
            }

            return (
                (state.event.hasDigitalRulesForSlideshows ||
                    state.eventAlbum.hasDigitalRulesForSlideshows) &&
                (state.event.hasAccessToPhotoDownloadDigitalRules ||
                    state.eventAlbum.hasAccessToPhotoDownloadDigitalRules)
            );
        }

        function hasRuleForPhotos(digitalRules) {
            return digitalRules.some(function withDigitalRule(digitalRule) {
                return digitalRule.appliesToPhotos !== false;
            });
        }

        function hasRuleForVideos(digitalRules) {
            return digitalRules.some(function withDigitalRule(digitalRule) {
                return digitalRule.appliesToVideos === true;
            });
        }

        function hasRuleForSlideshows(digitalRules) {
            return digitalRules.some(function withDigitalRule(digitalRule) {
                return digitalRule.appliesToSlideshows === true;
            });
        }

        /**
         * Which media types a rule delivers with no download limit at all. Mirrors the studio
         * wizard's getUnlimitedMediaTypes (free-digital-rule-wizard-v2/details/mappers.ts), which
         * is what makes the Download All toggle available in the first place - a media type
         * carrying a limit is deliberately not part of a Download All.
         *
         * The limits are read as falsy rather than compared to null: DigitalRuleTransformer sends
         * all three through transformToCount, so "unlimited" reaches the client as 0, not null.
         *
         * @param {Object} digitalRule
         * @returns {{photos: boolean, videos: boolean, slideshows: boolean}}
         */
        function getUnlimitedMediaTypes(digitalRule) {
            return {
                photos: digitalRule.appliesToPhotos !== false && !digitalRule.downloadLimit,
                videos: digitalRule.appliesToVideos === true && !digitalRule.videoDownloadLimit,
                slideshows:
                    digitalRule.appliesToSlideshows === true && !digitalRule.slideshowDownloadLimit
            };
        }

        /**
         * Whether a rule delivers any of what the page actually holds. Partial coverage counts: a
         * photos-only rule over a gallery of photos and videos still hands the visitor the photos,
         * and the videos are dropped from the bundle the same way they are today. Only a rule that
         * leaves with nothing at all is worth keeping off the page.
         *
         * @param {{photos: boolean, videos: boolean, slideshows: boolean}} unlimitedMediaTypes
         * @param {{photoCount: number, videoCount: number, slideshowCount?: number}} mediaCounts
         * @returns {boolean}
         */
        function coversAnyPresentMedia(unlimitedMediaTypes, mediaCounts) {
            // A count we were not given is a media type the page does not carry: countDownloadMedia
            // only counts the media the gallery can actually download.
            const { photoCount = 0, videoCount = 0, slideshowCount = 0 } = mediaCounts;

            return (
                (photoCount > 0 && unlimitedMediaTypes.photos) ||
                (videoCount > 0 && unlimitedMediaTypes.videos) ||
                (slideshowCount > 0 && unlimitedMediaTypes.slideshows)
            );
        }

        /**
         * The items of a Download All that the chosen rule actually delivers, dropping the media
         * types that carry a limit.
         *
         * maskCountsToRule() below already tells the visitor this is what they are getting, and the
         * studio is told the same when they enable the action ("The Download All action only
         * includes media with unlimited downloads"). Until this existed neither promise was kept -
         * the counts were masked but the payload was not, so a rule with unlimited photos and
         * limited videos sent the videos along anyway.
         *
         * Only for a Download All over a list of items the page is holding (Favorites, Search,
         * Tags). A whole-gallery or album Download All never sends ids at all and is filtered
         * server-side instead, and a single video or slideshow download is not a Download All - it
         * is the thing that spends the limit, so it must arrive unfiltered.
         *
         * @param {Array<Object>} photos Gallery items the page asked to download
         * @param {Object} digitalRule The rule the visitor picked
         * @returns {Array<Object>}
         */
        function filterToUnlimitedMedia(photos, digitalRule) {
            const unlimitedMediaTypes = getUnlimitedMediaTypes(digitalRule);

            return photos.filter(function isUnlimited(photo) {
                if (photo.type === 'video') {
                    return unlimitedMediaTypes.videos;
                }

                if (photo.type === 'slideshow') {
                    return unlimitedMediaTypes.slideshows;
                }

                return unlimitedMediaTypes.photos;
            });
        }

        /**
         * The counts, minus the media types a rule does not deliver, so a modal can name what the
         * visitor actually gets rather than what the gallery holds: a slideshows-only rule over a
         * mixed gallery names "1 Slideshow", not "11 Photos, 2 Videos and 1 Slideshow".
         *
         * @param {{photoCount: number, videoCount: number, slideshowCount?: number}} mediaCounts
         * @param {Object} digitalRule
         * @returns {{photoCount: number, videoCount: number, slideshowCount: number}}
         */
        function maskCountsToRule(mediaCounts, digitalRule) {
            const unlimitedMediaTypes = getUnlimitedMediaTypes(digitalRule);
            const masked = { photoCount: 0, videoCount: 0, slideshowCount: 0 };

            if (unlimitedMediaTypes.photos) {
                masked.photoCount = mediaCounts.photoCount || 0;
            }

            if (unlimitedMediaTypes.videos) {
                masked.videoCount = mediaCounts.videoCount || 0;
            }

            if (unlimitedMediaTypes.slideshows) {
                masked.slideshowCount = mediaCounts.slideshowCount || 0;
            }

            return masked;
        }

        /**
         * Whether any Download All rule delivers any of what the page holds.
         *
         * Counts are undefined whenever the contents are not knowable (e.g. Home listing album
         * cards rather than items), and nothing is hidden on a guess.
         *
         * @param {Array<{photos: boolean, videos: boolean, slideshows: boolean}>} downloadAllRuleMediaTypes
         * @param {{photoCount: number, videoCount: number, slideshowCount?: number}=} mediaCounts
         * @returns {boolean}
         */
        function hasDownloadAllRuleForMedia(downloadAllRuleMediaTypes, mediaCounts) {
            if (!mediaCounts) {
                return true;
            }

            return downloadAllRuleMediaTypes.some(function withUnlimitedMediaTypes(
                unlimitedMediaTypes
            ) {
                return coversAnyPresentMedia(unlimitedMediaTypes, mediaCounts);
            });
        }

        /**
         * The unlimited media types of every rule that offers Download All.
         *
         * @param {Array<Object>} digitalRules
         * @returns {Array<{photos: boolean, videos: boolean, slideshows: boolean}>}
         */
        function getDownloadAllRuleMediaTypes(digitalRules) {
            return digitalRules
                .filter(function isDownloadAllDigitalRule(digitalRule) {
                    return digitalRule.downloadAll;
                })
                .map(getUnlimitedMediaTypes);
        }

        $rootScope.$watch('userState.isEventContact', function withIsEventContact(isEventContact) {
            if (isEventContact) {
                state.isEventContact = isEventContact;

                enableEventLevelDigitalRuleAccess();
            }
        });

        $rootScope.$on(EVENT_EVENTS.GOT, function withEvent(_, event) {
            const digitalRules = event.digitalRules.items;
            const hasOnlyEventContactDigitalRules = digitalRules.every(
                isDigitalRuleForEventContact
            );
            const hasOnlyEventContactZipBundleDigitalRules = digitalRules
                .filter(function isZipBundleDigitalRule(digitalRule) {
                    return digitalRule.downloadAll;
                })
                .every(isDigitalRuleForEventContact);

            state.digitalRuleCount += digitalRules.length;
            state.event.hasDigitalRules = digitalRules.length > 0;
            state.event.hasDigitalRulesForPhotos = hasRuleForPhotos(digitalRules);
            state.event.hasDigitalRulesForVideos = hasRuleForVideos(digitalRules);
            state.event.hasDigitalRulesForSlideshows = hasRuleForSlideshows(digitalRules);
            state.event.hasDigitalRulesForDownloadAll = hasDownloadAllDigitalRules(digitalRules);
            state.event.downloadAllRuleMediaTypes = getDownloadAllRuleMediaTypes(digitalRules);
            state.event.hasPinForDigitalRules = checkForDigitalRulePinPresence(digitalRules);
            state.event.maximumDownloadAllPhotoCount =
                event.digitalRules.meta.downloadAllMaximumPhotoCount;

            if (
                !hasOnlyEventContactDigitalRules ||
                (hasOnlyEventContactDigitalRules && state.isEventContact)
            ) {
                enableEventLevelDigitalRuleAccess();
            }

            if (hasOnlyEventContactZipBundleDigitalRules && !state.isEventContact) {
                state.event.hasAccessToZipBundleDigitalRules = false;
            }

            function isDigitalRuleForEventContact(digitalRule) {
                return digitalRule.isForEventContact;
            }

            $rootScope.isDownloadPhotoPossible = canDownloadPhoto();
            $rootScope.isDownloadVideoPossible = canDownloadVideo();
            $rootScope.isDownloadSlideshowPossible = canDownloadSlideshow();
            $rootScope.isDownloadAllPossible = $rootScope.canDownload();
        });

        $rootScope.$on(EVENT_ALBUM_EVENTS.GOT, function withEventAlbum(_, eventAlbum) {
            const digitalRules = eventAlbum.digitalRules.items;

            state.digitalRuleCount += digitalRules.length;
            state.eventAlbum.hasDigitalRules = digitalRules.length > 0;
            state.eventAlbum.hasDigitalRulesForPhotos = hasRuleForPhotos(digitalRules);
            state.eventAlbum.hasDigitalRulesForVideos = hasRuleForVideos(digitalRules);
            state.eventAlbum.hasDigitalRulesForSlideshows = hasRuleForSlideshows(digitalRules);
            state.eventAlbum.hasDigitalRulesForDownloadAll =
                hasDownloadAllDigitalRules(digitalRules);
            state.eventAlbum.downloadAllRuleMediaTypes = getDownloadAllRuleMediaTypes(digitalRules);
            state.eventAlbum.hasPinForDigitalRules = checkForDigitalRulePinPresence(digitalRules);
            state.eventAlbum.maximumDownloadAllPhotoCount =
                eventAlbum.digitalRules.meta.downloadAllMaximumPhotoCount;

            if (
                state.eventAlbum.hasDigitalRulesForDownloadAll ||
                state.eventAlbum.hasPinForDigitalRules
            ) {
                state.eventAlbum.hasAccessToZipBundleDigitalRules = true;
                state.eventAlbum.hasAccessToPhotoDownloadDigitalRules = true;
            }

            if (state.eventAlbum.hasDigitalRules) {
                state.eventAlbum.hasAccessToPhotoDownloadDigitalRules = true;
            }

            $rootScope.isDownloadPhotoPossible = canDownloadPhoto();
            $rootScope.isDownloadVideoPossible = canDownloadVideo();
            $rootScope.isDownloadSlideshowPossible = canDownloadSlideshow();
            $rootScope.isDownloadAllPossible = $rootScope.canDownload();
        });

        $rootScope.$on(DIGITAL_RULE_EVENTS.LISTED, function withDigitalRules(_, digitalRules) {
            let shouldTryToDownload = true;

            state.digitalRules = digitalRules;

            if (!requiresPins()) {
                if (!hasDownloadAllDigitalRules(state.digitalRules)) {
                    state.event.hasAccessToZipBundleDigitalRules = false;
                    state.eventAlbum.hasAccessToZipBundleDigitalRules = false;

                    if (
                        state.pendingDownload.type === DOWNLOAD_TYPE.ALL ||
                        state.pendingDownload.type === DOWNLOAD_TYPE.ALBUM
                    ) {
                        shouldTryToDownload = false;
                    }
                }

                if (digitalRules.length === 0) {
                    state.event.hasAccessToPhotoDownloadDigitalRules = false;
                    state.eventAlbum.hasAccessToPhotoDownloadDigitalRules = false;

                    if (
                        state.pendingDownload.type === DOWNLOAD_TYPE.PHOTO ||
                        state.pendingDownload.type === DOWNLOAD_TYPE.VIDEO ||
                        state.pendingDownload.type === DOWNLOAD_TYPE.SLIDESHOW
                    ) {
                        shouldTryToDownload = false;
                    }
                }
            }

            if (
                state.digitalRuleCount === 1 &&
                state.digitalRules.length === 1 &&
                getRuleDownloadLimit(state.digitalRules[0]) === null
            ) {
                state.selectedDigitalRule = state.digitalRules[0];
            }

            afterPinCapturedCallback(getDigitalRules());

            afterPinCapturedCallback = noop;

            if (isCapturingPin && shouldTryToDownload) {
                tryToDownload();
            } else {
                SPModal.close().then(shouldTryToDownload ? tryToDownload : null);
            }
        });

        $rootScope.$on(DIGITAL_RULE_EVENTS.LIST_ERROR, function clearEmailOnGdprDenial() {
            state.credentials.email = null;
            spClientCredentials.clear();
        });

        $rootScope.$on(ZIP_BUNDLE_EVENTS.CREATED, function withResponse(_, response) {
            state.credentials.email = response.email;
            spClientCredentials.set({ zipBundleKey: response.zipBundleKey });

            countSlideshowDownloadAgainstRule();

            SPModal.close().then(function consumeSelectedDigitalRuleAndOpenZipBundleCreatedModal() {
                state.pendingDownload = {};
                state.selectedDigitalRule = null;

                $window.open(response.url, '_blank');
            });
        });

        $rootScope.$on(ZIP_BUNDLE_EVENTS.ERRORS.ALBUM, clearDigitalRulesAndTryToDownload);
        $rootScope.$on(ZIP_BUNDLE_EVENTS.ERRORS.PIN, undoDigitalRuleSelectionAndCaptureCredentials);
        $rootScope.$on(ZIP_BUNDLE_EVENTS.ERRORS.TOKEN_INVALID, cancelDownload);
        $rootScope.$on(ZIP_BUNDLE_EVENTS.ERRORS.ALL_MEDIA_LIMITED, handleAllMediaLimited);

        // Poll for Google Photos export status until the export is complete:
        $rootScope.$on(GOOGLE_PHOTOS_EXPORT_EVENTS.GOT, (_, response) => {
            if (!response.googlePhotosUrl) {
                setTimeout(() => {
                    $rootScope.$emit(GOOGLE_PHOTOS_EXPORT_EVENTS.GET, {
                        id: response.exportId,
                        authorizationToken: state.eventAuthorizationToken
                    });
                }, 1000);
            }
        });

        $rootScope.$on(GOOGLE_PHOTOS_EXPORT_EVENTS.ERRORS.UNAUTHORIZED, () => {
            spToast.toast({
                title: translateFilter('downloadUnknownError'),
                status: 'error',
                isClosable: true
            });
        });

        $rootScope.$on(GOOGLE_PHOTOS_EXPORT_EVENTS.ERRORS.UNKNOWN, () => {
            spToast.toast({
                title: translateFilter('downloadUnknownError'),
                status: 'error',
                isClosable: true
            });
        });

        $rootScope.$on(PHOTO_DOWNLOAD_EVENTS.CREATED, function withPhotoDownload(_, photoDownload) {
            if (
                state.selectedDigitalRule?.downloadLimit > 0 &&
                state.selectedDigitalRule?.downloadLimitUsed !==
                    photoDownload.digitalRule?.downloadLimitUsed
            ) {
                const digitalRule = findBy('id', state.selectedDigitalRule.id, state.digitalRules);

                digitalRule.downloadLimitUsed = photoDownload.digitalRule?.downloadLimitUsed;
                digitalRule.downloadedPhotoIds.push(state.pendingDownload.data.photoId);
            }

            SPModal.close().then(() => {
                state.pendingDownload = {};
                state.selectedDigitalRule = null;
            });

            const toastOptions = {
                title: translateFilter('downloadSuccessful'),
                status: 'success',
                isClosable: true
            };

            if (getNumberOfDownloadsRemaining(photoDownload.digitalRule) > 0) {
                toastOptions['description'] = translateFilter(
                    'freeDigitalsRemainingDownloadLimit',
                    {
                        count: getNumberOfDownloadsRemaining(photoDownload.digitalRule),
                        name: photoDownload.digitalRule.name
                    }
                );
            }

            if (state.downloadForRender) {
                SPModal.close().then(() => {
                    SPModal.open('photo-download', {
                        partialDirectory: 'free-digitals',
                        modalData: {
                            renderPhoto: photoDownload,
                            closeModal: function closeModal() {
                                SPModal.close();
                                cancelDownload();
                            }
                        }
                    });
                });
            } else {
                spToast.toast(toastOptions);
                $window.location.assign(photoDownload.downloadUrl);
            }
        });

        $rootScope.$on(PHOTO_DOWNLOAD_EVENTS.ERRORS.ALBUM, clearDigitalRulesAndTryToDownload);
        $rootScope.$on(
            PHOTO_DOWNLOAD_EVENTS.ERRORS.PIN,
            undoDigitalRuleSelectionAndCaptureCredentials
        );
        $rootScope.$on(PHOTO_DOWNLOAD_EVENTS.ERRORS.TIMEOUT, handleDownloadError);

        $rootScope.$on(VIDEO_DOWNLOAD_EVENTS.CREATED, function withVideoDownload(_, videoDownload) {
            if (
                state.selectedDigitalRule?.videoDownloadLimit > 0 &&
                state.selectedDigitalRule?.videoDownloadLimitUsed !==
                    videoDownload.digitalRule?.videoDownloadLimitUsed
            ) {
                const digitalRule = findBy('id', state.selectedDigitalRule.id, state.digitalRules);

                digitalRule.videoDownloadLimitUsed =
                    videoDownload.digitalRule?.videoDownloadLimitUsed;
                digitalRule.downloadedVideoIds = digitalRule.downloadedVideoIds || [];
                digitalRule.downloadedVideoIds.push(state.pendingDownload.data.photoId);
            }

            // Compute remaining before closing the modal clears the pending download type,
            // which getNumberOfDownloadsRemaining relies on to read the video limit fields.
            const numberOfDownloadsRemaining = getNumberOfDownloadsRemaining(
                videoDownload.digitalRule
            );

            SPModal.close().then(() => {
                state.pendingDownload = {};
                state.selectedDigitalRule = null;
            });

            const toastOptions = {
                title: translateFilter('downloadSuccessful'),
                status: 'success',
                isClosable: true
            };

            if (numberOfDownloadsRemaining > 0) {
                toastOptions['description'] = translateFilter(
                    'freeDigitalsRemainingDownloadLimit',
                    {
                        count: numberOfDownloadsRemaining,
                        name: videoDownload.digitalRule.name
                    }
                );
            }

            spToast.toast(toastOptions);
            $window.location.assign(videoDownload.downloadUrl);
        });

        $rootScope.$on(VIDEO_DOWNLOAD_EVENTS.ERRORS.ALBUM, clearDigitalRulesAndTryToDownload);
        $rootScope.$on(
            VIDEO_DOWNLOAD_EVENTS.ERRORS.PIN,
            undoDigitalRuleSelectionAndCaptureCredentials
        );
        $rootScope.$on(VIDEO_DOWNLOAD_EVENTS.ERRORS.TIMEOUT, handleDownloadError);

        $rootScope.$emit(EVENT_EVENTS.GET);
        getEventAlbumDigitalRulePresence();

        $rootScope.$on('$routeChangeSuccess', function updateEventAlbumDigitalRulePresence() {
            state.digitalRules = null;
            state.digitalRuleCount = 0;
            state.eventAlbum.hasDigitalRules = false;
            state.eventAlbum.hasDigitalRulesForPhotos = false;
            state.eventAlbum.hasDigitalRulesForVideos = false;
            state.eventAlbum.hasDigitalRulesForSlideshows = false;
            state.eventAlbum.hasDigitalRulesForDownloadAll = false;
            state.eventAlbum.downloadAllRuleMediaTypes = [];
            state.eventAlbum.hasPinForDigitalRules = false;
            state.eventAlbum.hasAccessToZipBundleDigitalRules = false;
            state.eventAlbum.hasAccessToPhotoDownloadDigitalRules = false;

            getEventAlbumDigitalRulePresence();
        });

        return {
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#canDownloadAll
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @param {integer} photoCount Number of photos to download
             * @param {{photoCount: number, videoCount: number, slideshowCount: number}=} mediaCounts
             *     What the page holds, so a rule that cannot deliver all of it does not offer
             *     Download All. Omitted when the contents are not knowable, which hides nothing.
             * @returns {boolean} True if user is allowed to download all photos
             */
            canDownloadAll: function canDownloadAll(photoCount, mediaCounts) {
                if (
                    state.event.maximumDownloadAllPhotoCount &&
                    photoCount > state.event.maximumDownloadAllPhotoCount
                ) {
                    return false;
                }

                return (
                    state.event.hasDigitalRulesForDownloadAll &&
                    state.event.hasAccessToZipBundleDigitalRules &&
                    hasDownloadAllRuleForMedia(state.event.downloadAllRuleMediaTypes, mediaCounts)
                );
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#canDownloadAlbum
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @param {integer} photoCount Number of photos to download
             * @param {{photoCount: number, videoCount: number, slideshowCount: number}=} mediaCounts
             *     What the album holds - see canDownloadAll.
             * @returns {boolean} True if user is allowed to download all photos in an album
             */
            canDownloadAlbum: function canDownloadAlbum(photoCount, mediaCounts) {
                if (!state.eventAlbum.hasDigitalRulesForDownloadAll && spHiddenAlbum.isInFlow()) {
                    return false;
                }

                const hasEventAlbumZipBundleDigitalRules =
                    state.eventAlbum.hasDigitalRulesForDownloadAll &&
                    state.eventAlbum.hasAccessToZipBundleDigitalRules &&
                    hasDownloadAllRuleForMedia(
                        state.eventAlbum.downloadAllRuleMediaTypes,
                        mediaCounts
                    );
                let isWithinEventAlbumPhotoCountThreshold = true;

                if (state.eventAlbum.maximumDownloadAllPhotoCount) {
                    isWithinEventAlbumPhotoCountThreshold =
                        photoCount <= state.eventAlbum.maximumDownloadAllPhotoCount;
                }

                return (
                    this.canDownloadAll(photoCount, mediaCounts) ||
                    (hasEventAlbumZipBundleDigitalRules && isWithinEventAlbumPhotoCountThreshold)
                );
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#canDownloadPhoto
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if user is allowed to download a photo
             */
            canDownloadPhoto,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#canDownloadVideo
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if user is allowed to download a video
             */
            canDownloadVideo,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#canDownloadSlideshow
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if user is allowed to download a slideshow
             */
            canDownloadSlideshow,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#downloadAll
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Will attempt to download all photos in the gallery
             *
             * @param {Array<Object>=} photos A subset of photos to download
             * @param {{photoCount: number, videoCount: number, slideshowCount: number}=} mediaCounts What the download
             *     contains, for the destination modal's copy. Kept apart from `photos`, which
             *     becomes the request's `photoIds` and spends the visitor's video allowance.
             */
            downloadAll: function downloadAll(photos, mediaCounts) {
                state.pendingDownload = {
                    type: DOWNLOAD_TYPE.ALL,
                    data: {
                        // TODO: Use 'Favorites' for the album name if the user is downloading their favorites?
                        googlePhotosAlbumName: $rootScope.eventData.name,
                        mediaCounts: mediaCounts,
                        photos: photos
                    }
                };

                tryToDownload();
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#downloadAlbum
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Will attempt to download all photos in the current album
             *
             * @param {Object} album An album
             * @param {string} album.id The album id
             * @param {string} album.name The album name
             * @param {{photoCount: number, videoCount: number, slideshowCount: number}=} mediaCounts What the download
             *     contains, for the destination modal's copy
             */
            downloadAlbum: function downloadAlbum(album, mediaCounts) {
                state.pendingDownload = {
                    type: DOWNLOAD_TYPE.ALBUM,
                    data: {
                        googlePhotosAlbumId: album.id,
                        googlePhotosAlbumName: album.name,
                        albumName: album.name,
                        mediaCounts: mediaCounts
                    }
                };

                tryToDownload();
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#downloadPhoto
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Will attempt to download the provided photo or current photo in view
             *
             * @param {integer=} photoId The id of the photo
             */
            downloadPhoto: function downloadPhoto(photoId) {
                state.pendingDownload = {
                    type: DOWNLOAD_TYPE.PHOTO,
                    data: {
                        photoId: photoId || Number($routeParams.photoId)
                    }
                };

                tryToDownload();
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#downloadVideo
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Will attempt to download the provided video or current video in view. Videos are
             * stored as photos with a video media type, so the id passed here is the photo id.
             *
             * @param {integer=} photoId The id of the video (photo) record
             */
            downloadVideo: function downloadVideo(photoId) {
                state.pendingDownload = {
                    type: DOWNLOAD_TYPE.VIDEO,
                    data: {
                        photoId: photoId || Number($routeParams.photoId)
                    }
                };

                tryToDownload();
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#downloadSlideshow
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Will attempt to download the provided slideshow or current slideshow in view.
             * Slideshows are stored as photos with a slideshow media type, so the id passed
             * here is the photo id. The download itself is performed via the zip bundle
             * endpoint, passing only this slideshow's photo id.
             *
             * @param {integer=} photoId The id of the slideshow (photo) record
             */
            downloadSlideshow: function downloadSlideshow(photoId) {
                state.pendingDownload = {
                    type: DOWNLOAD_TYPE.SLIDESHOW,
                    data: {
                        photoId: photoId || Number($routeParams.photoId)
                    }
                };

                tryToDownload();
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#isTryingToDownloadAll
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if currently trying to download all photos in the gallery
             */
            isTryingToDownloadAll: function isTryingToDownloadAll() {
                return state.pendingDownload.type === DOWNLOAD_TYPE.ALL;
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#isTryingToDownloadAlbum
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if currently trying to download all photos in the current album
             */
            isTryingToDownloadAlbum: function isTryingToDownloadAlbum() {
                return state.pendingDownload.type === DOWNLOAD_TYPE.ALBUM;
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#isTryingToDownloadPhoto
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if currently trying to download the current photo in view
             */
            isTryingToDownloadPhoto: function isTryingToDownloadPhoto() {
                return state.pendingDownload.type === DOWNLOAD_TYPE.PHOTO;
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#isTryingToDownloadVideo
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if currently trying to download a single video
             */
            isTryingToDownloadVideo,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#isTryingToDownloadSlideshow
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if currently trying to download a single slideshow
             */
            isTryingToDownloadSlideshow,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getRuleDownloadLimit
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Reads the download-limit field appropriate to the current pending download's
             * media type - a digital rule carries separate limits for photos, videos and
             * slideshows, so callers must not read `digitalRule.downloadLimit` directly.
             *
             * @param {Object} digitalRule A digital rule
             * @returns {number|null} The applicable limit, or null if unlimited
             */
            getRuleDownloadLimit,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getRuleDownloadedIds
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Reads the list of already-downloaded ids appropriate to the current pending
             * download's media type (photo/video/slideshow each have their own list).
             *
             * @param {Object} digitalRule A digital rule
             * @returns {Array<number>}
             */
            getRuleDownloadedIds,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getNumberOfDownloadsRemaining
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Number of downloads left under the digital rule's limit for the current pending
             * download's media type, clipped at zero.
             *
             * @param {Object} digitalRule A digital rule
             * @returns {number|null} Downloads remaining, or null if the rule has no limit
             */
            getNumberOfDownloadsRemaining,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getPendingDownloadData
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {Object|null} Pending download data
             */
            getPendingDownloadData: function getPendingDownloadData() {
                return state.pendingDownload.data ? angular.copy(state.pendingDownload.data) : null;
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getPendingDownloadPhotoId
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * The id of the single photo, video or slideshow being downloaded. Prefer this over
             * getPendingDownloadData in React renders, which deep-copies the photo array.
             *
             * @returns {number|undefined} The pending photo id, if this is a single-item download
             */
            getPendingDownloadPhotoId: function getPendingDownloadPhotoId() {
                return state.pendingDownload.data ? state.pendingDownload.data.photoId : undefined;
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getPendingDownloadMediaCounts
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * What the pending download contains. Undefined when the counts are not knowable.
             * Prefer this over getPendingDownloadData, which deep-copies the photo array on every
             * React render.
             *
             * @returns {{photoCount: number, videoCount: number, slideshowCount: number}|undefined}
             */
            getPendingDownloadMediaCounts: function getPendingDownloadMediaCounts() {
                if (!state.pendingDownload.data) {
                    return undefined;
                }

                return state.pendingDownload.data.mediaCounts;
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getPendingDownloadMediaCountsForCurrentRule
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * What the pending download will actually deliver, so the destination modal names the
             * visitor's download rather than the gallery: a slideshows-only rule over a mixed
             * gallery names the slideshow alone.
             *
             * Unmasked until a rule is chosen, so nothing is named on a guess.
             *
             * @returns {{photoCount: number, videoCount: number, slideshowCount: number}|undefined}
             */
            getPendingDownloadMediaCountsForCurrentRule:
                function getPendingDownloadMediaCountsForCurrentRule() {
                    const mediaCounts = this.getPendingDownloadMediaCounts();

                    if (!mediaCounts || !state.selectedDigitalRule) {
                        return mediaCounts;
                    }

                    return maskCountsToRule(mediaCounts, state.selectedDigitalRule);
                },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#doesRuleCoverAnyPendingMedia
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Whether this rule delivers any of what the pending download holds. Keeps the rule
             * list in step with the Download All button: a rule that hands the visitor nothing at
             * all should not be offered, while one that covers only part of a mixed gallery still
             * should - the rest is dropped from the bundle, as it is today.
             *
             * True whenever the contents are not knowable, so nothing is hidden on a guess.
             *
             * @param {Object} digitalRule
             * @returns {boolean}
             */
            doesRuleCoverAnyPendingMedia: function doesRuleCoverAnyPendingMedia(digitalRule) {
                const mediaCounts = this.getPendingDownloadMediaCounts();

                if (!mediaCounts) {
                    return true;
                }

                return coversAnyPresentMedia(getUnlimitedMediaTypes(digitalRule), mediaCounts);
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getEmail
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {string|null} Current email in credentials
             */
            getEmail: function getEmail() {
                return state.credentials.email;
            },
            canExportToGoogle,
            disconnectFromGoogle,
            getGoogleEmail,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#hasPins
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {boolean} True if any of the current digital rules has a PIN
             */
            hasPins: requiresPins,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#getDigitalRules
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @returns {Array<Object>} Array of currently applicable free digital rules
             */
            getDigitalRules,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#captureAnotherPin
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Prepares the service for capturing another PIN to fetch digital rules with
             * #setCredentials
             *
             * @param {function=} afterPinCaptured Invoked with the new digital rules after the PIN
             *     has been captured
             */
            captureAnotherPin: function captureAnotherPin(afterPinCaptured = noop) {
                afterPinCapturedCallback = afterPinCaptured;
                isCapturingPin = true;

                delete state.digitalRules;
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#selectDigitalRule
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Sets the digital rule and continues to generate the request for the current download
             *
             * @param {Object} digitalRule A digital rule
             */
            selectDigitalRule: function selectDigitalRule(digitalRule, downloadDestination) {
                state.selectedDigitalRule = digitalRule;
                state.pendingDownload.destination = downloadDestination;
                $rootScope.requiredDestinationSelectionType = null;
                $rootScope.requiredDestinationSelectionPhotoId = null;

                tryToDownload();
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#setAuthorizationToken
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Sets the authorization token value for zip bundle requests
             *
             * @param {string} authorizationToken An authorization token
             */
            setAuthorizationToken: function setAuthorizationToken(authorizationToken) {
                state.authorizationToken = authorizationToken;
            },
            setEventAuthorizationToken,
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#setCredentials
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @description
             * Sets the credentials and continues to generate the request for the current download
             *
             * @param {Object} credentials Credentials
             * @param {string} credentials.email An email address
             * @param {boolean} credentials.doesAcceptTerms a checkbox
             * @param {string=} credentials.pin A PIN
             */
            setCredentials: function setCredentials(credentials) {
                state.credentials.email = credentials.email;
                state.credentials.doesAcceptTerms = credentials.doesAcceptTerms;

                if (
                    credentials.pin &&
                    credentials.pin.length >= PIN_LENGTH.MINIMUM &&
                    credentials.pin.length <= PIN_LENGTH.MAXIMUM
                ) {
                    state.credentials.pins.push(credentials.pin);
                }

                tryToDownload();
            },
            /**
             * @ngdoc method
             * @name sp.client.gallery.service:spFreeDigitals#hasSelectedADigitalRule
             * @methodOf sp.client.gallery.service:spFreeDigitals
             *
             * @return {boolean} True if user has selected a digital rule
             */
            hasSelectedADigitalRule: function hasSelectedADigitalRule() {
                return state.selectedDigitalRule !== null;
            },
            /**
             * Sets the download destination for the current pending download
             * The valid strings are in DOWNLOAD_DESTINATION from Sp/Gallery
             * @param {string} destination
             */
            setDestination: function setDestination(destination) {
                state.pendingDownload.destination = destination;
                $rootScope.requiredDestinationSelectionType = null;
                $rootScope.requiredDestinationSelectionPhotoId = null;

                tryToDownload();
            },
            /**
             * Get the number of remaining download for the currently selected digital rule.
             * If no rule is currently selected, return null
             * @returns {number|null}
             */
            getRemainingDownloadsForCurrentRule: function getRemainingDownloadsForCurrentRule() {
                return getNumberOfDownloadsRemaining(state.selectedDigitalRule);
            },
            /**
             * Clear all information regarding the current pending download
             */
            cancelDownload
        };

        function cancelDownload() {
            state.pendingDownload = {};
            state.selectedDigitalRule = null;
            $rootScope.requiredDestinationSelectionType = null;
            $rootScope.requiredDestinationSelectionPhotoId = null;
        }

        // Slideshows are delivered through the zip bundle endpoint, which - unlike the photo and
        // video download endpoints - does not return the rule's refreshed counts, so the counts
        // held here have to be advanced by hand or the limit only applies after a page reload.
        // The request logs exactly one download for this slideshow and the server counts distinct
        // ids, so a re-download of the same slideshow does not consume another one.
        function countSlideshowDownloadAgainstRule() {
            if (!isTryingToDownloadSlideshow() || !state.selectedDigitalRule) {
                return;
            }

            const photoId = state.pendingDownload.data && state.pendingDownload.data.photoId;
            const digitalRule = findBy('id', state.selectedDigitalRule.id, state.digitalRules);

            // Without a limit there is nothing to count, and the API omits the downloaded ids.
            if (!digitalRule || !digitalRule.slideshowDownloadLimit || !photoId) {
                return;
            }

            digitalRule.downloadedSlideshowIds = digitalRule.downloadedSlideshowIds || [];

            if (digitalRule.downloadedSlideshowIds.includes(photoId)) {
                return;
            }

            digitalRule.downloadedSlideshowIds.push(photoId);
            digitalRule.slideshowDownloadLimitUsed =
                (digitalRule.slideshowDownloadLimitUsed ?? 0) + 1;
        }

        function isTryingToDownloadVideo() {
            return state.pendingDownload.type === DOWNLOAD_TYPE.VIDEO;
        }

        function isTryingToDownloadSlideshow() {
            return state.pendingDownload.type === DOWNLOAD_TYPE.SLIDESHOW;
        }

        // The same digital rule carries separate limits for photos, videos and slideshows.
        // These helpers read the correct field based on the current download type
        // so the shared download/auto-select logic works for all of them.
        function getRuleDownloadLimit(digitalRule) {
            if (!digitalRule) {
                return null;
            }

            if (isTryingToDownloadVideo()) {
                return digitalRule.videoDownloadLimit;
            }

            if (isTryingToDownloadSlideshow()) {
                return digitalRule.slideshowDownloadLimit;
            }

            return digitalRule.downloadLimit;
        }

        function getRuleDownloadLimitUsed(digitalRule) {
            if (!digitalRule) {
                return null;
            }

            if (isTryingToDownloadVideo()) {
                return digitalRule.videoDownloadLimitUsed;
            }

            if (isTryingToDownloadSlideshow()) {
                return digitalRule.slideshowDownloadLimitUsed;
            }

            return digitalRule.downloadLimitUsed;
        }

        function getRuleDownloadedIds(digitalRule) {
            if (!digitalRule) {
                return [];
            }

            if (isTryingToDownloadVideo()) {
                return digitalRule.downloadedVideoIds || [];
            }

            if (isTryingToDownloadSlideshow()) {
                return digitalRule.downloadedSlideshowIds || [];
            }

            return digitalRule.downloadedPhotoIds || [];
        }

        function getNumberOfDownloadsRemaining(digitalRule) {
            const downloadLimit = getRuleDownloadLimit(digitalRule);

            if (!digitalRule || downloadLimit === null) {
                return null;
            }

            // The used count may be absent for videos until it is provided by the API; treat a
            // missing count as zero used so a limited rule is not falsely reported as exhausted.
            // The download endpoint remains the source of truth and enforces the real limit.
            const downloadLimitUsed = getRuleDownloadLimitUsed(digitalRule) ?? 0;
            const numRemaining = downloadLimit - downloadLimitUsed;

            if (numRemaining < 0) {
                return 0;
            }

            return numRemaining;
        }

        function getEventAlbumDigitalRulePresence() {
            const currentAlbumId = getAlbumId();

            if (currentAlbumId) {
                $rootScope.$emit(EVENT_ALBUM_EVENTS.GET, currentAlbumId);
            }
        }

        function getAlbumId() {
            const { albumId } = $routeParams;

            if (albumId && albumId !== 'all') {
                return albumId;
            }
        }

        function getGooglePhotosPhotoIds() {
            // A single video or slideshow download, which is what spends the rule's limit for that
            // media type - so it is sent whole, never filtered.
            if (state.pendingDownload.data.photoId !== undefined) {
                return [state.pendingDownload.data.photoId];
            }

            if (state.pendingDownload.data.photos instanceof Array) {
                return filterToUnlimitedMedia(
                    state.pendingDownload.data.photos,
                    state.selectedDigitalRule
                ).map((photo) => photo.id);
            }

            return undefined;
        }

        function hasDownloadAllDigitalRules(digitalRules) {
            return digitalRules.some(function withDigitalRule(digitalRule) {
                return digitalRule.downloadAll;
            });
        }

        // Returns the digital rules that apply to the current download's media type.
        // A single rule may apply to any combination of photos, videos and slideshows, so a
        // photo download must only consider rules with `appliesToPhotos`, a video download
        // only those with `appliesToVideos`, and a slideshow download only those with
        // `appliesToSlideshows`. Album/all (zip) downloads are unchanged and use every rule.
        function getApplicableDigitalRules() {
            const digitalRules = state.digitalRules || [];

            // Videos and slideshows are newer capabilities, so a rule only applies to them when
            // explicitly enabled. Photos remain applicable unless a rule explicitly opts out,
            // preserving behavior for rules created before the photo/video distinction existed.
            if (isTryingToDownloadVideo()) {
                return digitalRules.filter((digitalRule) => digitalRule.appliesToVideos === true);
            }

            if (isTryingToDownloadSlideshow()) {
                return digitalRules.filter(
                    (digitalRule) => digitalRule.appliesToSlideshows === true
                );
            }

            if (state.pendingDownload.type === DOWNLOAD_TYPE.PHOTO) {
                return digitalRules.filter((digitalRule) => digitalRule.appliesToPhotos !== false);
            }

            return digitalRules;
        }

        function checkForDigitalRulePinPresence(digitalRules) {
            return digitalRules.some(function withDigitalRule(digitalRule) {
                return digitalRule.hasDownloadPin;
            });
        }

        function canExportToGoogle() {
            // Single-photo export stays disabled: it spends one Google Photos API request per
            // item and exhausted the daily project quota (PR #17005). A video is one export, so
            // it is safe to allow. A slideshow download is also one export, and is supported.
            return state.pendingDownload.type !== DOWNLOAD_TYPE.PHOTO;
        }

        function requiresPins() {
            return state.event.hasPinForDigitalRules || state.eventAlbum.hasPinForDigitalRules;
        }

        async function disconnectFromGoogle() {
            // Request a new client access token without a google_authorized_email and google_photos_export_id:

            await spAuthorizationToken.getAuthorizationTokenForEvent(true, {
                email: state.credentials.email
            });
        }

        function getGoogleEmail() {
            if ($rootScope.isSignedIntoGoogle) {
                return $rootScope.googleEmail ?? null;
            }

            return null;
        }

        /**
         * This function can be called several times for a single download as there can be several times where the user needs to interact
         * to continue with the download.
         * @returns void
         */
        function tryToDownload() {
            // Check if the user needs to enter an email address
            if (requiresCredentials()) {
                captureCredentials();
                return;
            }

            spClientCredentials.set({ email: state.credentials.email });

            // Check if the digital rules need to be fetched
            if (!state.digitalRules) {
                const doesAcceptTerms =
                    state.credentials.doesAcceptTerms || spAppData.get('userData').doesAcceptTerms;

                $rootScope.$emit(DIGITAL_RULE_EVENTS.LIST, {
                    albumId: getAlbumId(),
                    email: state.credentials.email,
                    pins: state.credentials.pins,
                    doesAcceptTerms
                });

                return;
            }

            const applicableDigitalRules = getApplicableDigitalRules();
            const isSingleMediaDownload =
                state.pendingDownload.type === DOWNLOAD_TYPE.PHOTO ||
                state.pendingDownload.type === DOWNLOAD_TYPE.VIDEO ||
                state.pendingDownload.type === DOWNLOAD_TYPE.SLIDESHOW;

            // No digital rule applies to this media type (e.g. a video with only photo rules):
            // there is nothing to download, so abort rather than opening an empty selection modal.
            if (isSingleMediaDownload && applicableDigitalRules.length === 0 && !requiresPins()) {
                SPModal.close().then(cancelDownload);

                return;
            }

            if (applicableDigitalRules.length === 1 && !requiresPins()) {
                // Handle single-rule, no-pin galleries (single applicable rule for this media type)
                const [digitalRule] = applicableDigitalRules;
                const canDownloadPhoto =
                    getRuleDownloadLimit(digitalRule) === null ||
                    getNumberOfDownloadsRemaining(digitalRule) > 0;
                const hasAlreadyDownloadedPhoto = getRuleDownloadedIds(digitalRule).includes(
                    state.pendingDownload.data.photoId
                );

                if (canDownloadPhoto || hasAlreadyDownloadedPhoto) {
                    state.selectedDigitalRule = digitalRule;

                    // Check if the user needs to pick a download destination
                    if (requiresDestinationSelection()) {
                        $rootScope.$evalAsync(() => {
                            $rootScope.requiredDestinationSelectionType =
                                state.pendingDownload.type;
                            $rootScope.requiredDestinationSelectionPhotoId =
                                state.pendingDownload.data.photoId;
                        });

                        return;
                    }
                } else {
                    spToast.toast({
                        title: translateFilter('downloadLimitReached'),
                        status: 'error',
                        isClosable: true
                    });

                    return;
                }
            } else if (requiresDigitalRuleSelection()) {
                if (isCapturingPin) {
                    isCapturingPin = false;
                } else {
                    // Allow the user to pick the digital rule to use
                    SPModal.open('select-digital-rule', { partialDirectory: 'free-digitals' });
                }

                return;
            }

            // Handle Google Photos downloads (album/all, and an individual video or slideshow
            // download; a single photo download does not offer this destination - see
            // canExportToGoogle())
            if (state.pendingDownload.destination === DOWNLOAD_DESTINATION.GOOGLE_PHOTOS) {
                const googlePhotosExportProperties = {
                    clientGalleryUrl: $rootScope.eventData.publicUrl,
                    digitalRuleId: state.selectedDigitalRule.id,
                    googlePhotosAlbumName: state.pendingDownload.data.googlePhotosAlbumName,
                    albumId: state.pendingDownload.data.googlePhotosAlbumId,
                    photoIds: getGooglePhotosPhotoIds()
                };

                // Store the export properties in local storage to be retrieved by GooglePhotosExportPage in a new tab.
                const authKey = crypto.randomUUID();
                localStorage.setItem(authKey, JSON.stringify(googlePhotosExportProperties));

                // Open GooglePhotosExportPage in a new tab to begin the export process, including Google Photos OAuth sign-in, if necessary:
                const statusPageUrl = `${$rootScope.eventData.publicUrl}/google-photos-export-view?authKey=${authKey}`;

                open(statusPageUrl, '_blank');

                // Listen for local storage to change. When the export window writes back an
                // updated token (either after Google OAuth or after reusing an existing
                // connection), propagate it to this window so subsequent exports use the
                // latest google_photos_export_id without re-authenticating.
                const storageListener = (event) => {
                    if (event.key === authKey && event.newValue) {
                        const eventStorageId =
                            $rootScope.eventData.publicId || $rootScope.eventData.id;

                        removeEventListener('storage', storageListener);
                        localStorage.removeItem(authKey);

                        const eventAuthorizationToken = event.newValue;

                        sessionStorage.setItem(
                            `clientGalleryAuthorizationTokenForEvent-${eventStorageId}`,
                            eventAuthorizationToken
                        );

                        setEventAuthorizationToken(eventAuthorizationToken);
                    }
                };

                addEventListener('storage', storageListener);

                state.pendingDownload = {};
                state.selectedDigitalRule = null;

                return;
            }

            // This modal is called SelectDigitalRule, but it is used for all downloads, even if a rule does not need to be selected
            SPModal.open('select-digital-rule', { partialDirectory: 'free-digitals' });

            // Perform the download action for device downloads
            if (state.pendingDownload.type === DOWNLOAD_TYPE.PHOTO) {
                $rootScope.$emit(PHOTO_DOWNLOAD_EVENTS.CREATE, {
                    authorizationToken: state.authorizationToken,
                    albumId: getAlbumId(),
                    photoId: state.pendingDownload.data.photoId,
                    digitalRuleId: state.selectedDigitalRule.id,
                    email: state.credentials.email,
                    pins: state.credentials.pins
                });
            } else if (state.pendingDownload.type === DOWNLOAD_TYPE.VIDEO) {
                $rootScope.$emit(VIDEO_DOWNLOAD_EVENTS.CREATE, {
                    authorizationToken: state.authorizationToken,
                    albumId: getAlbumId(),
                    photoId: state.pendingDownload.data.photoId,
                    digitalRuleId: state.selectedDigitalRule.id,
                    email: state.credentials.email,
                    pins: state.credentials.pins
                });
            } else {
                // Slideshow downloads are handled entirely through the zip bundle endpoint, the
                // same way album/all downloads are, but with only the selected slideshow's
                // photo id instead of every selected photo's id.
                const photoIds = isTryingToDownloadSlideshow()
                    ? [state.pendingDownload.data.photoId]
                    : pluck(
                          'id',
                          filterToUnlimitedMedia(
                              state.pendingDownload.data.photos || [],
                              state.selectedDigitalRule
                          )
                      );

                // A list where every item carries a limit leaves nothing to send, and no ids is
                // how the server is asked for the whole gallery - so stop rather than send that.
                if (state.pendingDownload.data.photos instanceof Array && photoIds.length === 0) {
                    SPModal.close().then(cancelDownload);

                    return;
                }

                $rootScope.$emit(ZIP_BUNDLE_EVENTS.CREATE, {
                    authorizationToken: state.authorizationToken,
                    albumId: getAlbumId(),
                    digitalRuleId: state.selectedDigitalRule.id,
                    email: state.credentials.email,
                    pins: state.credentials.pins,
                    photoIds
                });
            }

            function requiresCredentials() {
                state.credentials.email =
                    state.credentials.email || spClientCredentials.get().readableEmail;

                return !state.credentials.email;
            }

            function requiresDestinationSelection() {
                return canExportToGoogle() && !state.pendingDownload.destination;
            }

            function requiresDigitalRuleSelection() {
                return !state.selectedDigitalRule;
            }
        }

        async function setEventAuthorizationToken(eventAuthorizationToken) {
            // Store the event-level authorization token, for use with requests to the Google Photos Export API.
            state.eventAuthorizationToken = eventAuthorizationToken;

            // Check if the user is signed into Google Photos, and set the isSignedIntoGoogle state as a result.
            const { apiRoot } = spAppData.get('urls');
            const url = `${apiRoot}/oauth/google/status`;

            const options = {
                headers: {
                    Authorization: `Bearer ${eventAuthorizationToken}`
                }
            };

            let data;

            try {
                const response = await fetch(url, options);
                data = await response.json();
            } catch (error) {
                // We can safely ignore this API request error.
                // If the request failed, and the user is not signed in already,
                // then clicking "Save to Google Photos" will prompt them again.
                return;
            }

            $rootScope.isSignedIntoGoogle = data.isSignedIntoGoogle;
            $rootScope.googleEmail = data.googleEmail;
        }

        function captureCredentials() {
            SPModal.open('capture-credentials', { partialDirectory: 'free-digitals' });
        }

        function clearDigitalRulesAndTryToDownload() {
            delete state.selectedDigitalRule;
            delete state.digitalRules;

            tryToDownload();
        }

        function getDigitalRules() {
            return angular.copy(state.digitalRules) || [];
        }

        function undoDigitalRuleSelectionAndCaptureCredentials() {
            delete state.selectedDigitalRule;
            delete state.digitalRules;

            SPModal.close().then(captureCredentials);
        }

        function handleDownloadError() {
            SPModal.close().then(function emitDownloadErrorToastEvent() {
                delete state.selectedDigitalRule;
                delete state.digitalRules;
            });
            // This $timout prevents the toast from getting closed by the SPModal
            $timeout(() => {
                spToast.toast({
                    title: translateFilter('downloadUnknownError'),
                    status: 'warning',
                    isClosable: true
                });
            }, 500);
        }

        // A Download All over a gallery or album where everything carries a limit under the
        // chosen rule - the server sends nothing rather than the limited media. The button can
        // still be offered when the page does not know what it holds (e.g. Home listing albums).
        function handleAllMediaLimited() {
            SPModal.close().then(cancelDownload);
            // This $timout prevents the toast from getting closed by the SPModal
            $timeout(() => {
                spToast.toast({
                    title: translateFilter('downloadAllMediaLimited'),
                    status: 'warning',
                    isClosable: true
                });
            }, 500);
        }

        function enableEventLevelDigitalRuleAccess() {
            if (state.event.hasDigitalRules) {
                state.event.hasAccessToPhotoDownloadDigitalRules = true;
            }

            if (state.event.hasDigitalRulesForDownloadAll) {
                state.event.hasAccessToZipBundleDigitalRules = true;
            }
        }
    }
];
